import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { PipBlockedMediaOverlay } from 'components/PiP/PipBlockedMediaOverlay';
import { usePiPWindow } from 'components/PiP/PiPProvider';
import { LayoutProvider } from 'features/layout/LayoutRoot/LayoutProvider';
import RoomGrid from 'features/layout/layouts/TiledWithContent/RoomGrid';
import { CustomTileHostProvider } from 'features/layout/tiles/custom-tile-host/CustomTileHostProvider';
import ActionNotificationContainer from 'features/notifications/components/ActionNotificationContainer';
import { PipToolbar } from 'features/toolbar/PipToolbar/PipToolbar';
import { selectToolbarWidth } from 'features/toolbar/toolbarSlice';
import { useRoomReactions } from 'features/room-reactions/useRoomReactions';
import ReactionsOverlay from 'features/room-reactions/components/ReactionsOverlay';
import { CaptionsContainer } from 'features/transcripts/slices/captions/CaptionsContainer';
import WatermarkOverlay from 'features/watermark/WatermarkOverlay';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';

import 'theme/icons.css';

const FlexBox = styled(Box)({
  display: 'flex',
  width: '100%',
  height: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const MIN_WIDTH = 280;
const MIN_HEIGHT = 90;

const Root = styled(Box)<{ fullscreenPanel: boolean }>(({ theme, fullscreenPanel }) => ({
  height: '100vh',
  width: '100vw',

  '& .fullscreen-panel': fullscreenPanel
    ? {
        position: 'absolute',
        top: `${theme.room.tileGap}px`,
        left: `${theme.room.tileGap}px`,
        width: `calc(100% - ${theme.room.tileGap * 2}px)`,
        height: `calc(100% - ${theme.room.tileGap * 2}px)`,
        zIndex: 1000,
      }
    : {},
}));

export const PipLayout = ({
  width: outerWidth,
  height: outerHeight,
}: {
  width: number;
  height: number;
}) => {
  const muiTheme = useTheme();
  const { t } = useTranslation('room');

  const toolbarWidth = useAppSelector(selectToolbarWidth);

  const [width, height] = useMemo(
    () => [outerWidth, outerHeight - toolbarWidth - muiTheme.room.tileGap],
    [outerHeight, outerWidth, toolbarWidth, muiTheme.room.tileGap]
  );

  const { pipWindow } = usePiPWindow();

  const { isFeatureEnabled: isRoomReactionsEnabled } = useRoomReactions();

  useEffect(() => {
    if (pipWindow) {
      pipWindow.document.body.style.backgroundColor = muiTheme.room.background;
      pipWindow.document.body.style.overflow = 'hidden';
    }
  }, [pipWindow, muiTheme.room.background]);

  return (
    <Root
      fullscreenPanel={false}
      sx={(th) => ({
        paddingBottom: th.toolbar.width,
        minWidth: MIN_WIDTH,
      })}
    >
      <Box
        onClick={(e) => {
          e.stopPropagation();
          window.focus();
        }}
      >
        <ActionNotificationContainer />
      </Box>
      <Box style={{ position: 'relative', height }}>
        <CustomTileHostProvider>
          <FlexBox
            sx={(theme) => ({
              padding: `${theme.room.tileGap}px`,
              position: 'relative',
            })}
          >
            <LayoutProvider size={{ width, height }}>
              <RoomGrid />
            </LayoutProvider>
          </FlexBox>
        </CustomTileHostProvider>
      </Box>
      <PipToolbar />
      {isRoomReactionsEnabled && <ReactionsOverlay pipMode />}
      <CaptionsContainer roomWidth={outerWidth} roomHeight={outerHeight} pipMode />
      <WatermarkOverlay />
      <PipBlockedMediaOverlay />
      {outerWidth < MIN_WIDTH || outerHeight < MIN_HEIGHT ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            top: '0',
            left: '0',
            position: 'fixed',
            zIndex: 9999,
            backgroundColor: muiTheme.room.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={(th) => ({
              color: th.palette.getContrastText(th.room.background),
            })}
          >
            {t('grid.pip_size_warning')}
          </Typography>
        </Box>
      ) : null}
    </Root>
  );
};
